import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Loader2, Mailbox, RefreshCw, Plug, Pencil, Trash2, AlertCircle, CheckCircle2, Tag as TagIcon } from "lucide-react";

const VACIO = {
  nombre: "",
  email: "",
  protocolo: "imap",
  proveedor_label: "GetYourGuide",
  imap_host: "imap.gmail.com",
  imap_port: 993,
  secret_env: "",
  carpeta: "INBOX",
  activo: true,
};

export default function BuzonesConfig() {
  const [buzones, setBuzones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(VACIO);
  const [saving, setSaving] = useState(false);
  const [pollJob, setPollJob] = useState(null);
  const pollRef = useRef(null);
  const polling = pollJob?.estado === "en_progreso";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/captura/buzones");
      setBuzones(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const pollJobOnce = useCallback(async () => {
    try {
      const { data } = await api.get("/captura/poll/estado");
      setPollJob(data);
      if (data.estado !== "en_progreso") {
        stopPolling();
        if (data.estado === "completado") {
          const r = data.resumen || {};
          toast.success(`Lectura completa: ${r.vistos || 0} vistos en la ventana, ${r.correos || 0} nuevos, ${r.reservas || 0} reservas.`);
        } else if (data.estado === "error") {
          toast.error(`Error en la lectura: ${data.error || "desconocido"}`);
        }
        load();
      }
    } catch (e) {
      /* noop */
    }
  }, [load]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollJobOnce();
    pollRef.current = setInterval(pollJobOnce, 2000);
  }, [pollJobOnce]);

  useEffect(() => {
    load();
    (async () => {
      try {
        const { data } = await api.get("/captura/poll/estado");
        setPollJob(data);
        if (data.estado === "en_progreso") startPolling();
      } catch (e) {
        /* noop */
      }
    })();
    return stopPolling;
  }, [load, startPolling]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const openNew = () => {
    setEditing(null);
    setForm(VACIO);
    setOpen(true);
  };
  const openEdit = (b) => {
    setEditing(b);
    setForm({ ...VACIO, ...b });
    setOpen(true);
  };

  const guardar = async () => {
    if (!form.nombre.trim() || !form.email.trim()) {
      toast.error("Completá nombre y email del buzón.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/captura/buzones/${editing.id}`, form);
        toast.success("Buzón actualizado.");
      } else {
        await api.post("/captura/buzones", form);
        toast.success("Buzón creado.");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActivo = async (b) => {
    try {
      await api.put(`/captura/buzones/${b.id}`, { activo: !b.activo });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const eliminar = async (b) => {
    try {
      await api.delete(`/captura/buzones/${b.id}`);
      toast.success("Buzón eliminado.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const probar = async (b) => {
    toast.info(`Probando conexión a ${b.email}...`);
    try {
      const { data } = await api.post(`/captura/buzones/${b.id}/test`);
      if (data.ok) toast.success("Conexión exitosa.");
      else toast.error(`Falló: ${data.error}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const pollNow = async () => {
    try {
      await api.post("/captura/poll-now");
      toast.info("Lectura de correos iniciada en segundo plano…");
      setPollJob({ estado: "en_progreso", progreso: {} });
      startPolling();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <p className="text-sm text-slate-500 max-w-2xl">
          Casillas que el sistema lee automáticamente. Las credenciales (contraseña de aplicación) se cargan
          como variables de entorno; aquí solo se indica el nombre de la variable.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={pollNow} disabled={polling} data-testid="poll-now-button" className="gap-2">
            {polling ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Leer ahora
          </Button>
          <Button onClick={openNew} data-testid="new-buzon-button" className="gap-2">
            <Plus className="h-4 w-4" /> Nuevo buzón
          </Button>
        </div>
      </div>

      {polling && (
        <Card className="p-3 bg-blue-50 border-blue-200" data-testid="poll-progress">
          <div className="flex items-center gap-2 text-sm text-blue-800">
            <Loader2 className="h-4 w-4 animate-spin shrink-0" />
            <span>
              Leyendo{pollJob?.progreso?.buzon_actual ? ` “${pollJob.progreso.buzon_actual}”` : "…"} ·{" "}
              {pollJob?.progreso?.vistos || 0} vistos en la ventana, {pollJob?.progreso?.correos || 0} nuevos,{" "}
              {pollJob?.progreso?.reservas || 0} reservas
            </span>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {buzones.map((b) => (
            <Card key={b.id} className="p-4" data-testid={`buzon-${b.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-100 text-slate-700 shrink-0">
                    <Mailbox className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 truncate">{b.nombre}</p>
                    <p className="text-[12px] text-slate-500 font-mono truncate">{b.email}</p>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      <EstadoBuzon b={b} />
                      <Tag>{b.protocolo === "imap" ? `IMAP · ${b.imap_host}` : "Gmail API"}</Tag>
                      <Tag>{b.proveedor_label}</Tag>
                      <Tag mono>{b.secret_env || "sin secret"}</Tag>
                    </div>
                    {b.last_polled && (
                      <p className="text-[11px] text-slate-400 mt-1">
                        Última lectura: {new Date(b.last_polled).toLocaleString("es")}
                        {b.ultimo_vistos_ventana != null && (
                          <span data-testid={`buzon-salud-${b.id}`}>
                            {" "}· {b.ultimo_vistos_ventana} vistos en la ventana, {b.ultimo_procesados || 0} procesados
                          </span>
                        )}
                      </p>
                    )}
                    {b.es_gmail && (
                      <p
                        className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1 flex-wrap"
                        data-testid={`buzon-etiqueta-${b.id}`}
                      >
                        <TagIcon className="h-3 w-3 text-slate-400" />
                        Etiqueta de la app:{" "}
                        <span className="font-medium text-slate-700">{b.etiqueta_actual}</span>
                        <span className="text-slate-300">·</span>
                        <span data-testid={`buzon-etiquetados-count-${b.id}`}>
                          {b.etiquetados_count || 0} etiquetados
                        </span>
                      </p>
                    )}
                  </div>
                </div>
                <Switch
                  checked={b.activo}
                  onCheckedChange={() => toggleActivo(b)}
                  data-testid={`buzon-activo-${b.id}`}
                />
              </div>
              <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-slate-100">
                <Button variant="ghost" size="sm" onClick={() => probar(b)} data-testid={`buzon-test-${b.id}`} className="gap-1 text-slate-600">
                  <Plug className="h-4 w-4" /> Probar
                </Button>
                <Button variant="ghost" size="icon" onClick={() => openEdit(b)} data-testid={`buzon-edit-${b.id}`} className="h-8 w-8 text-slate-500">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => eliminar(b)} data-testid={`buzon-delete-${b.id}`} className="h-8 w-8 text-slate-500 hover:text-red-600">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="buzon-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Mailbox className="h-5 w-5" /> {editing ? "Editar buzón" : "Nuevo buzón"}
            </DialogTitle>
            <DialogDescription>
              Configurá la casilla a monitorear. Cargá la contraseña de aplicación en la variable de entorno
              indicada en “Secret env”.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Nombre</Label>
                <Input value={form.nombre} onChange={(e) => set({ nombre: e.target.value })} data-testid="buzon-nombre-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Email</Label>
                <Input value={form.email} onChange={(e) => set({ email: e.target.value })} disabled={!!editing} data-testid="buzon-email-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Protocolo</Label>
                <Select value={form.protocolo} onValueChange={(v) => set({ protocolo: v })}>
                  <SelectTrigger data-testid="buzon-protocolo-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="imap">IMAP (app password)</SelectItem>
                    <SelectItem value="gmail_api">Gmail API</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Proveedor / etiqueta</Label>
                <Input value={form.proveedor_label} onChange={(e) => set({ proveedor_label: e.target.value })} data-testid="buzon-proveedor-input" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs">Servidor IMAP</Label>
                <Input value={form.imap_host} onChange={(e) => set({ imap_host: e.target.value })} data-testid="buzon-host-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Puerto</Label>
                <Input type="number" value={form.imap_port} onChange={(e) => set({ imap_port: parseInt(e.target.value, 10) || 993 })} data-testid="buzon-port-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Secret env (variable)</Label>
                <Input value={form.secret_env} onChange={(e) => set({ secret_env: e.target.value })} placeholder="BUZON_INTERTRAVEL_PWD" data-testid="buzon-secret-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Carpeta</Label>
                <Input value={form.carpeta} onChange={(e) => set({ carpeta: e.target.value })} data-testid="buzon-carpeta-input" />
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Switch checked={form.activo} onCheckedChange={(v) => set({ activo: v })} data-testid="buzon-form-activo" />
              <Label className="text-sm">Activo (incluido en el polling)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="buzon-form-cancel">Cancelar</Button>
            <Button onClick={guardar} disabled={saving} data-testid="buzon-form-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Guardar" : "Crear buzón"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Tag({ children, mono, tone = "slate", ...rest }) {
  const tones = {
    slate: "bg-slate-50 text-slate-600 border-slate-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <span {...rest} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border ${tones[tone]} ${mono ? "font-mono" : ""}`}>
      {children}
    </span>
  );
}

function EstadoBuzon({ b }) {
  if (!b.credenciales_cargadas)
    return (
      <Tag tone="amber" data-testid={`buzon-estado-${b.id}`}>
        <AlertCircle className="h-3 w-3" /> No conectado · pendiente de credenciales
      </Tag>
    );
  if (b.last_error)
    return (
      <Tag tone="red" data-testid={`buzon-estado-${b.id}`}>
        <AlertCircle className="h-3 w-3" /> Error de conexión
      </Tag>
    );
  if (!b.activo)
    return (
      <Tag tone="slate" data-testid={`buzon-estado-${b.id}`}>
        <CheckCircle2 className="h-3 w-3" /> Credenciales OK (inactivo)
      </Tag>
    );
  return (
    <Tag tone="green" data-testid={`buzon-estado-${b.id}`}>
      <CheckCircle2 className="h-3 w-3" /> Conectado
    </Tag>
  );
}
